import React, { useState, useEffect } from "react";
import PageInstructions from "@/components/PageInstructions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import jsPDF from 'jspdf';

const EsquemaInfalivel = () => {
  const [valorAposta, setValorAposta] = useState(1);
  const [rodadaAtual, setRodadaAtual] = useState(1);
  const [showResults, setShowResults] = useState(false);
  const [anotacoes, setAnotacoes] = useState("");
  const [gruposSimulacao, setGruposSimulacao] = useState(10);

  const gruposEsquema = [
    {numero: 1, nome: 'Avestruz', emoji: '🦓'},
    {numero: 2, nome: 'Águia', emoji: '🦅'},
    {numero: 3, nome: 'Burro', emoji: '🫏'},
    {numero: 9, nome: 'Cobra', emoji: '🐍'},
    {numero: 10, nome: 'Coelho', emoji: '🐰'},
    {numero: 11, nome: 'Cavalo', emoji: '🐎'},
    {numero: 14, nome: 'Gato', emoji: '🐱'},
    {numero: 17, nome: 'Macaco', emoji: '🐵'},
    {numero: 18, nome: 'Porco', emoji: '🐷'},
    {numero: 20, nome: 'Peru', emoji: '🦃'}
  ];

  // Load saved data on component mount
  useEffect(() => {
    const anotacoesSalvas = localStorage.getItem('esquema_infalivel_notas');
    const configSalva = localStorage.getItem('esquema_infalivel_config');

    if (anotacoesSalvas) {
      setAnotacoes(anotacoesSalvas);
    }

    if (configSalva) {
      const config = JSON.parse(configSalva);
      setValorAposta(config.valorAposta || 1);
      setRodadaAtual(config.rodadaAtual || 1);
    }
  }, []);

  const calcularApostas = () => {
    setShowResults(true);
  };

  const salvarAnotacoes = () => {
    const dados = {
      valorAposta,
      rodadaAtual
    };

    localStorage.setItem('esquema_infalivel_notas', anotacoes);
    localStorage.setItem('esquema_infalivel_config', JSON.stringify(dados));
    alert('✅ Anotações salvas com sucesso!');
  };

  const limparTudo = () => {
    if (confirm('⚠️ Tem certeza que deseja limpar todas as anotações e configurações?')) {
      setAnotacoes('');
      setValorAposta(1);
      setRodadaAtual(1);
      setShowResults(false);
      
      localStorage.removeItem('esquema_infalivel_notas');
      localStorage.removeItem('esquema_infalivel_config');
      
      alert('🗑️ Dados limpos com sucesso!');
    }
  };

  const exportarDados = () => {
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleString('pt-BR');
    
    // Header
    doc.setFontSize(20);
    doc.text('Martingale Ajustado - 10 Grupos', 20, 20);
    
    doc.setFontSize(12);
    doc.text(`Data de Exportação: ${currentDate}`, 20, 35);
    doc.text(`Valor da Aposta: R$ ${valorAposta.toFixed(2)}`, 20, 45);
    doc.text(`Rodada Atual: ${rodadaAtual}ª`, 20, 55);
    
    let yPos = 75;
    
    // Grupos do esquema
    doc.setFontSize(14);
    doc.text('Grupos do Esquema:', 20, yPos);
    yPos += 15;
    
    gruposEsquema.forEach((grupo) => {
      doc.setFontSize(10);
      doc.text(`${grupo.numero.toString().padStart(2, '0')} - ${grupo.nome}`, 20, yPos);
      yPos += 10;
    });
    
    yPos += 20;
    
    // Anotações
    if (anotacoes) {
      doc.setFontSize(14);
      doc.text('Anotações:', 20, yPos);
      
      const splitNotes = doc.splitTextToSize(anotacoes, 170);
      doc.setFontSize(10);
      doc.text(splitNotes, 20, yPos + 15);
    }
    
    doc.save(`martingale_ajustado_${new Date().toISOString().split('T')[0]}.pdf`);
    alert('📤 PDF exportado com sucesso!');
  };

  // Calculate values for current bet (NEW FORMULA)
  const valorPorGrupo = valorAposta * Math.pow(2, rodadaAtual - 1);
  const totalInvestido = valorPorGrupo * 10; // 10 grupos instead of 15
  const retornoEsperado = valorPorGrupo * 20;
  const lucroLiquido = retornoEsperado - (getTotalInvestidoAcumulado(rodadaAtual));

  // Calculate accumulated investment up to current round
  function getTotalInvestidoAcumulado(rodada: number) {
    let total = 0;
    for (let i = 1; i <= rodada; i++) {
      const valorRodada = valorAposta * Math.pow(2, i - 1);
      total += valorRodada * 10;
    }
    return total;
  }

  // Generate progression table data (NEW LOGIC)
  const progressionData = [];
  let investimentoAcumulado = 0;
  for (let rodada = 1; rodada <= 8; rodada++) {
    const valorRodada = valorAposta * Math.pow(2, rodada - 1);
    const totalRodada = valorRodada * 10; // 10 grupos
    investimentoAcumulado += totalRodada;
    const retornoRodada = valorRodada * 20;
    const lucroRodada = retornoRodada - investimentoAcumulado; // Always R$ 10.00 profit

    progressionData.push({
      rodada,
      valorPorGrupo: valorRodada,
      totalInvestido: totalRodada,
      investimentoAcumulado,
      retorno: retornoRodada,
      lucro: lucroRodada
    });
  }

  // Simulation for different group numbers
  const simularGrupos = (numGrupos: number) => {
    const dados = [];
    let acumulado = 0;
    for (let rodada = 1; rodada <= 6; rodada++) {
      const valor = valorAposta * Math.pow(2, rodada - 1);
      const total = valor * numGrupos;
      acumulado += total;
      const retorno = valor * 20;
      const lucro = retorno - acumulado;
      
      dados.push({
        rodada,
        valor,
        total,
        acumulado,
        retorno,
        lucro,
        chanceAcerto: (numGrupos / 25) * 100
      });
    }
    return dados;
  };

  return (
    <div className="w-full max-w-full space-y-6 overflow-x-auto">
      <PageInstructions
        title="📖 Como usar o Martingale Ajustado"
        steps={[
          "O sistema seleciona automaticamente 10 grupos para você (40% de chance)",
          "Defina o valor da sua aposta inicial",
          "Veja a tabela com a progressão de apostas para cada rodada",
          "Se perder, aumente a aposta conforme a tabela — se ganhar, volte ao início",
          "O sistema calcula automaticamente o investimento total e o lucro esperado",
          "Exporte a tabela em PDF para acompanhar suas apostas"
        ]}
      />
      {/* Header */}
      <div className="relative bg-gradient-to-br from-green-500 to-green-600 text-white p-8 rounded-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-50 animate-pulse"></div>
        <div className="relative z-10 text-center space-y-4">
          <h1 className="text-4xl font-bold text-shadow-lg">🎯 Martingale Ajustado</h1>
          <p className="text-xl opacity-95">Sistema otimizado com 10 grupos - 40% de chance de acerto</p>
          <div className="inline-block bg-white/20 px-6 py-3 rounded-full font-bold text-lg border-2 border-white/30">
            ✅ "Lucro garantido de R$ 10,00 a cada acerto!"
          </div>
        </div>
      </div>

      {/* Why Use This Method */}
      <Card className="border-2 border-blue-400 bg-gradient-to-br from-blue-100 to-cyan-100">
        <CardHeader>
          <CardTitle className="text-blue-700 text-2xl flex items-center gap-3 font-bold">
            💡 Por Que Usar Este Método?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-bold text-lg text-gray-800">🎯 Vantagens Comprovadas:</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center gap-2"><span className="text-green-500">✓</span> <strong>40% de chance</strong> de acerto por rodada</li>
                <li className="flex items-center gap-2"><span className="text-green-500">✓</span> <strong>Menor risco</strong> de prejuízo acumulado</li>
                <li className="flex items-center gap-2"><span className="text-green-500">✓</span> <strong>Lucro fixo</strong> de R$ 10,00 sempre que acertar</li>
                <li className="flex items-center gap-2"><span className="text-green-500">✓</span> <strong>Caixa controlado</strong> e mais seguro</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h4 className="font-bold text-lg text-gray-800">📊 Matemática do Sucesso:</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center gap-2"><span className="text-orange-500">⚡</span> <strong>10 grupos</strong> ao invés de 15</li>
                <li className="flex items-center gap-2"><span className="text-orange-500">⚡</span> <strong>Recuperação rápida</strong> com apenas 1 acerto</li>
                <li className="flex items-center gap-2"><span className="text-orange-500">⚡</span> <strong>Sustentável</strong> mesmo em sequências de perdas</li>
                <li className="flex items-center gap-2"><span className="text-orange-500">⚡</span> <strong>Pagamento 20:1</strong> otimizado</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Strategy Explanation */}
      <Card className="border-2 border-orange-400 bg-gradient-to-br from-orange-100 to-yellow-100">
        <CardHeader>
          <CardTitle className="text-orange-700 text-2xl flex items-center gap-3 font-bold">
            🧠 Como Funciona a Estratégia Otimizada
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border-2 border-orange-400 rounded-xl p-6 text-center hover:transform hover:scale-105 transition-all duration-300 shadow-lg">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">1</div>
              <h4 className="font-bold mb-2 text-gray-800">Aposte R$ 1,00</h4>
              <p className="text-sm text-gray-700 font-medium">Em cada um dos 10 grupos selecionados. Total: R$ 10,00 por rodada.</p>
            </div>
            <div className="bg-white border-2 border-orange-400 rounded-xl p-6 text-center hover:transform hover:scale-105 transition-all duration-300 shadow-lg">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">2</div>
              <h4 className="font-bold mb-2 text-gray-800">40% de Chance</h4>
              <p className="text-sm text-gray-700 font-medium">Alta probabilidade de acerto a cada sorteio</p>
            </div>
            <div className="bg-white border-2 border-orange-400 rounded-xl p-6 text-center hover:transform hover:scale-105 transition-all duration-300 shadow-lg">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">3</div>
              <h4 className="font-bold mb-2 text-gray-800">Lucro: R$ 10,00</h4>
              <p className="text-sm text-gray-700 font-medium">Ganho fixo sempre que acertar</p>
            </div>
            <div className="bg-white border-2 border-orange-400 rounded-xl p-6 text-center hover:transform hover:scale-105 transition-all duration-300 shadow-lg">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">4</div>
              <h4 className="font-bold mb-2 text-gray-800">Não Saiu?</h4>
              <p className="text-sm text-gray-700 font-medium">Dobre a aposta na próxima rodada</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Animals Section */}
      <Card className="border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="text-blue-600 text-2xl text-center flex items-center justify-center gap-3">
            🦁 Os 10 Grupos Selecionados (40% de Chance)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4 mb-6">
            {gruposEsquema.map((grupo) => (
              <div key={grupo.numero} className="bg-white border-2 border-blue-300 rounded-xl p-4 text-center hover:bg-gradient-to-r hover:from-blue-500 hover:to-blue-600 hover:text-white transition-all duration-300 cursor-pointer group">
                <div className="text-2xl font-bold text-red-500 group-hover:text-white mb-1">{grupo.numero.toString().padStart(2, '0')}</div>
                <div className="text-sm font-bold">{grupo.emoji} {grupo.nome}</div>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-700 italic font-medium">
            💰 <strong>Pagamento:</strong> Cada grupo paga 20 para 1<br />
            🎯 <strong>Estratégia:</strong> Com 10 grupos você tem 40% de chance de acerto por sorteio
          </p>
        </CardContent>
      </Card>

      {/* Calculator Section */}
      <Card className="border-2 border-green-300 bg-gradient-to-br from-green-50 to-emerald-50">
        <CardHeader>
          <CardTitle className="text-green-600 text-2xl text-center">📊 Calculadora de Apostas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="font-bold text-gray-800 text-lg">💰 Valor por Grupo:</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 font-medium">R$</span>
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={valorAposta}
                    onChange={(e) => setValorAposta(parseFloat(e.target.value) || 1)}
                    placeholder="1.00"
                    className="w-full pl-12 pr-4 py-4 border-2 border-green-400 rounded-lg text-center text-lg bg-white text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <span className="text-gray-800 font-medium">reais</span>
              </div>
            </div>
            <div className="space-y-2">
              <label className="font-bold text-gray-800 text-lg">🎮 Rodada Atual:</label>
              <input
                type="number"
                min="1"
                value={rodadaAtual}
                onChange={(e) => setRodadaAtual(parseInt(e.target.value) || 1)}
                className="w-full p-4 border-2 border-green-400 rounded-lg text-center text-lg bg-white text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          <Button
            onClick={calcularApostas}
            className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-4 text-lg rounded-full font-bold"
          >
            🔢 Calcular Apostas
          </Button>

          {showResults && (
            <div className="bg-white border-2 border-green-400 rounded-lg p-6 space-y-4 animate-in slide-in-from-bottom-4 duration-500">
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-gray-800 font-medium">Valor por Grupo:</span>
                <span className="font-bold text-red-500">R$ {valorPorGrupo.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-gray-800 font-medium">Total Investido (10 grupos):</span>
                <span className="font-bold text-red-500">R$ {totalInvestido.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-gray-800 font-medium">Retorno se Acertar:</span>
                <span className="font-bold text-red-500">R$ {retornoEsperado.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-gray-800 font-medium">Chance de Acerto:</span>
                <span className="font-bold text-blue-500">40%</span>
              </div>
              <div className="flex justify-between items-center py-3 font-bold text-lg">
                <span className="text-gray-800">Lucro Líquido:</span>
                <span className="text-green-500">R$ {lucroLiquido > 0 ? lucroLiquido.toFixed(2) : '10.00'}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Martingale Section */}
      <Card className="border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-red-50">
        <CardHeader>
          <CardTitle className="text-orange-600 text-2xl text-center">📈 Progressão Martingale Ajustado</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] border-collapse bg-white rounded-lg overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
                  <th className="p-4 text-center font-bold">Rodada</th>
                  <th className="p-4 text-center font-bold">Valor por Grupo</th>
                  <th className="p-4 text-center font-bold">Total Aposta</th>
                  <th className="p-4 text-center font-bold">Acumulado Investido</th>
                  <th className="p-4 text-center font-bold">Retorno (1 acerto)</th>
                  <th className="p-4 text-center font-bold">Lucro</th>
                </tr>
              </thead>
              <tbody>
                {progressionData.map((row, index) => (
                  <tr key={index} className="hover:bg-orange-50 bg-green-50">
                    <td className="p-4 text-center font-bold text-gray-800">{row.rodada}ª</td>
                    <td className="p-4 text-center text-gray-800">R$ {row.valorPorGrupo.toFixed(2)}</td>
                    <td className="p-4 text-center text-gray-800">R$ {row.totalInvestido.toFixed(2)}</td>
                    <td className="p-4 text-center text-gray-800">R$ {row.investimentoAcumulado.toFixed(2)}</td>
                    <td className="p-4 text-center text-gray-800">R$ {row.retorno.toFixed(2)}</td>
                    <td className="p-4 text-center font-bold text-green-600">
                      R$ 10,00
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-center mt-4 text-sm text-gray-600 font-medium">
            ✨ <strong>Observação:</strong> O lucro é sempre R$ 10,00 independente da rodada!
          </p>
        </CardContent>
      </Card>

      {/* Simulation Section */}
      <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-violet-50">
        <CardHeader>
          <CardTitle className="text-purple-600 text-2xl text-center">🎯 Simulação Automática</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4 justify-center">
            <label className="font-bold text-gray-800">Número de Grupos:</label>
            <select 
              value={gruposSimulacao} 
              onChange={(e) => setGruposSimulacao(parseInt(e.target.value))}
              className="p-2 border-2 border-purple-400 rounded-lg bg-white"
            >
              <option value={8}>8 grupos (32%)</option>
              <option value={10}>10 grupos (40%)</option>
              <option value={12}>12 grupos (48%)</option>
            </select>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] border-collapse bg-white rounded-lg overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-gradient-to-r from-purple-500 to-violet-500 text-white">
                  <th className="p-2 sm:p-3 text-center text-sm sm:text-base font-bold">Rodada</th>
                  <th className="p-2 sm:p-3 text-center text-sm sm:text-base font-bold">Valor/Grupo</th>
                  <th className="p-2 sm:p-3 text-center text-sm sm:text-base font-bold">Total</th>
                  <th className="p-2 sm:p-3 text-center text-sm sm:text-base font-bold">Acumulado</th>
                  <th className="p-2 sm:p-3 text-center text-sm sm:text-base font-bold">Retorno</th>
                  <th className="p-2 sm:p-3 text-center text-sm sm:text-base font-bold">Lucro</th>
                  <th className="p-2 sm:p-3 text-center text-sm sm:text-base font-bold">Chance</th>
                </tr>
              </thead>
              <tbody>
                {simularGrupos(gruposSimulacao).map((row, index) => (
                  <tr key={index} className="hover:bg-purple-50 border-b border-gray-100">
                    <td className="p-2 sm:p-3 text-center font-bold text-gray-800 text-sm sm:text-base">{row.rodada}ª</td>
                    <td className="p-2 sm:p-3 text-center text-gray-800 text-sm sm:text-base">R$ {row.valor.toFixed(2)}</td>
                    <td className="p-2 sm:p-3 text-center text-gray-800 text-sm sm:text-base">R$ {row.total.toFixed(2)}</td>
                    <td className="p-2 sm:p-3 text-center text-gray-800 text-sm sm:text-base">R$ {row.acumulado.toFixed(2)}</td>
                    <td className="p-2 sm:p-3 text-center text-gray-800 text-sm sm:text-base">R$ {row.retorno.toFixed(2)}</td>
                    <td className={`p-2 sm:p-3 text-center font-bold text-sm sm:text-base ${row.lucro > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      R$ {row.lucro > 0 ? '10,00' : row.lucro.toFixed(2)}
                    </td>
                    <td className="p-2 sm:p-3 text-center font-bold text-blue-600 text-sm sm:text-base">{row.chanceAcerto.toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Warning Box */}
      <Card className="border-2 border-red-300 bg-gradient-to-br from-red-50 to-pink-50">
        <CardHeader>
          <CardTitle className="text-red-600 text-xl flex items-center gap-3">
            ⚠️ Importante - Gerenciamento de Risco
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 ml-4 text-gray-800 leading-relaxed font-medium">
            <li><strong>Capital controlado:</strong> Método otimizado requer menos capital que o tradicional</li>
            <li><strong>Alta chance de recuperação:</strong> 40% de probabilidade de acerto por rodada</li>
            <li><strong>Lucro fixo:</strong> Sempre R$ 10,00 de ganho quando acertar</li>
            <li><strong>Sustentável:</strong> Progressão mais segura mesmo em sequências de perdas</li>
            <li><strong>Responsabilidade:</strong> Jogue sempre com consciência e dentro dos seus limites</li>
          </ul>
        </CardContent>
      </Card>

      {/* Notes Section */}
      <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-violet-50">
        <CardHeader>
          <CardTitle className="text-purple-600 text-xl">📝 Suas Anotações e Estratégias:</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <textarea
            value={anotacoes}
            onChange={(e) => setAnotacoes(e.target.value)}
            placeholder="Anote aqui suas observações sobre:
- Resultados dos jogos anteriores
- Quais grupos têm saído com mais frequência
- Padrões observados nos horários
- Controle de ganhos e perdas
- Ajustes na estratégia..."
            className="w-full min-h-32 p-4 border-2 border-purple-400 rounded-lg text-sm resize-y bg-white text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
          
          <div className="flex flex-wrap gap-4 justify-center">
            <Button onClick={salvarAnotacoes} className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700">
              💾 Salvar Anotações
            </Button>
            <Button onClick={limparTudo} variant="outline" className="border-gray-400 hover:bg-gray-100">
              🗑️ Limpar Tudo
            </Button>
            <Button onClick={exportarDados} className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700">
              📤 Exportar PDF
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EsquemaInfalivel;